import { ActionIcon, Badge, Button, Group, Loader, Menu, ScrollArea, Stack, Table, Text, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconDotsVertical, IconDownload, IconEdit, IconGripVertical, IconPlus, IconQrcode, IconRefresh, IconSelector, IconTrash } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import { apiClient, useFloorPlans, useReorderTables, useTables, useWaiterOptions } from "../../../shared/api";
import { useAction, useRemove } from "../../../shared/api/resources";
import { MoneyDisplay, PageHeader, ResourceForm, StatusBadge, confirmDelete, type FormField } from "../../../shared/ui";

// Backend QR PDF/PNG endpoint'inden blob indirip tarayıcıda dosya olarak kaydet.
// (PurchaseOrders.tsx downloadPoPdf deseni — tek kaynaktan üretim.)
async function downloadBlob(url: string, filename: string, successMsg: string) {
  try {
    const res = await apiClient.get(url, { responseType: "blob" });
    const objUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = objUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objUrl);
    notifications.show({ message: successMsg, color: "green" });
  } catch {
    notifications.show({ message: "PDF üretilemedi", color: "red" });
  }
}

const SHAPE_OPTIONS = [
  { value: "SQUARE", label: "Kare" },
  { value: "RECTANGLE", label: "Dikdörtgen" },
  { value: "ROUND", label: "Yuvarlak" },
  { value: "OVAL", label: "Oval" },
];

const STATE_OPTIONS = [
  { value: "FREE", label: "Boş" },
  { value: "RESERVED", label: "Rezerve" },
  { value: "OCCUPIED", label: "Dolu" },
  { value: "CLEANING", label: "Temizleniyor" },
  { value: "OUT_OF_SERVICE", label: "Servis Dışı" },
];

// Durum sıralama önceliği (Boş → Rezerve → Dolu → …)
const STATE_ORDER: Record<string, number> = {
  FREE: 0,
  RESERVED: 1,
  OCCUPIED: 2,
  CLEANING: 3,
  OUT_OF_SERVICE: 4,
};

type SortKey = "sort_order" | "name" | "capacity" | "state" | "plan" | "is_active";
type SortDir = "asc" | "desc";

// "M2" < "M10" — sayı içeren adlar için doğal (natural) karşılaştırma
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: boolean;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const Icon = !active ? IconSelector : dir === "asc" ? IconChevronUp : IconChevronDown;
  return (
    <Table.Th>
      <UnstyledButton onClick={() => onSort(sortKey)} aria-label={`${label} sırala`}>
        <Group gap={4} wrap="nowrap">
          <span>{label}</span>
          <Icon size={14} style={{ opacity: active ? 1 : 0.4 }} />
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

export function TablesPage() {
  const { data, isLoading } = useTables();
  const { data: plansData } = useFloorPlans();
  const { data: waiterOptions } = useWaiterOptions();
  const remove = useRemove("tables");
  const reorder = useReorderTables();
  const regenerateQr = useAction("tables", "regenerate-qr");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("sort_order");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  // Sürüklenen satırın id'si — sürükleme görsel geri bildirimi için.
  const [dragId, setDragId] = useState<string | null>(null);

  const list: any[] = data?.results || data || [];
  const plans: any[] = plansData?.results || plansData || [];
  const waiters: Array<{ value: string; label: string }> = waiterOptions || [];

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sortedList = useMemo(() => {
    const planName = (id: any) => plans.find((p) => p.id === id)?.name || "";
    const copy = [...list];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "sort_order":
          cmp = (a.sort_order ?? 0) - (b.sort_order ?? 0);
          break;
        case "name":
          cmp = collator.compare(a.name || "", b.name || "");
          break;
        case "capacity":
          cmp = (a.capacity ?? 0) - (b.capacity ?? 0);
          break;
        case "state":
          cmp = (STATE_ORDER[a.state] ?? 99) - (STATE_ORDER[b.state] ?? 99);
          break;
        case "plan":
          cmp = collator.compare(planName(a.floor_plan), planName(b.floor_plan));
          break;
        case "is_active":
          cmp = Number(a.is_active) - Number(b.is_active);
          break;
      }
      // Eşitlik durumunda ada göre stabil sıra
      if (cmp === 0) cmp = collator.compare(a.name || "", b.name || "");
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [list, plans, sortKey, sortDir]);

  // Sürükle-bırak yalnızca "Sıra" kolonuna göre artan sıralıyken anlamlı —
  // başka kolona göre dizilmişken satır taşımak sort_order'ı bozar.
  const dragEnabled = sortKey === "sort_order" && sortDir === "asc";

  // Sürükleme sırasında anlık yeniden dizilen yerel satır listesi.
  // sortedList değişince (veri yenilenince) tekrar senkronlanır.
  const [rows, setRows] = useState<any[]>(sortedList);
  useEffect(() => {
    setRows(sortedList);
  }, [sortedList]);

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from === -1 || to === -1) {
      setDragId(null);
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    setDragId(null);
    // Backend'e yeni id sırasını gönder — sort_order 0,1,2,… atanır.
    reorder.mutate(
      next.map((r) => r.id),
      {
        onSuccess: () =>
          notifications.show({ message: "Sıralama kaydedildi", color: "green" }),
        onError: () => {
          notifications.show({ message: "Sıralama kaydedilemedi", color: "red" });
          setRows(sortedList); // başarısızsa sunucu durumuna geri dön
        },
      },
    );
  }

  const fields: FormField[] = [
    { name: "name", label: "Ad", required: true, placeholder: "M1, Bahçe-3, …" },
    { name: "capacity", label: "Kapasite (kişi)", type: "number", min: 1, defaultValue: 4 },
    {
      name: "sort_order",
      label: "Sıra",
      type: "number",
      min: 0,
      defaultValue: 0,
      description: "Masalar tablosunda gösterim sırası — küçük değer üstte (eşitlikte ada göre)",
    },
    {
      name: "shape",
      label: "Şekil",
      type: "select",
      options: SHAPE_OPTIONS,
      defaultValue: "SQUARE",
    },
    {
      name: "floor_plan",
      label: "Salon Planı",
      type: "select",
      options: plans.map((p) => ({ value: p.id, label: p.name })),
      clearable: true,
      placeholder: plans.length === 0 ? "Önce Plan Editörü'nden plan oluşturun" : "Seçin",
    },
    {
      name: "assigned_waiter",
      label: "İlgili Garson",
      type: "select",
      options: waiters,
      clearable: true,
      description: "Masaya sabit garson ataması — QR onay ekranı bu masayı atanan garsona göre renklendirir",
      placeholder: waiters.length === 0 ? "Henüz garson tanımlı değil" : "Garson seçin (opsiyonel)",
    },
    {
      name: "state",
      label: "Durum",
      type: "select",
      options: STATE_OPTIONS,
      defaultValue: "FREE",
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
    {
      name: "deposit_override_minor",
      label: "Kapora Override (boş = tenant default)",
      type: "money",
      min: 0,
    },
  ];

  async function handleDelete(t: any) {
    if (!(await confirmDelete(`"${t.name}" masası silinsin mi?`))) return;
    try {
      await remove.mutateAsync(t.id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Silinemedi",
        color: "red",
      });
    }
  }

  function downloadTableQr(t: any) {
    downloadBlob(
      `/api/v1/tables/${t.id}/qr-pdf/`,
      `qr-${t.name}.pdf`,
      `"${t.name}" QR kodu indirildi`,
    );
  }

  function downloadAllQr() {
    // Sunucu kendi filtresini uygular; burada aktif salon-planı filtresi yok
    // (tabloda hepsi gösteriliyor), bu yüzden tüm masalar tek PDF'te iner.
    downloadBlob(
      "/api/v1/tables/qr-pdf/",
      "masa-qr-kodlari.pdf",
      "Tüm masaların QR PDF'i indirildi",
    );
  }

  async function handleRegenerateQr(t: any) {
    if (
      !(await confirmDelete(
        `"${t.name}" masasının QR kodu yenilensin mi? Bu masaya ait ESKİ BASILI QR'lar geçersiz olacak.`,
      ))
    ) {
      return;
    }
    try {
      await regenerateQr.mutateAsync({ id: t.id });
      notifications.show({
        message: `"${t.name}" QR kodu yenilendi — yeni QR'ı bastırın`,
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "QR yenilenemedi",
        color: "red",
      });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Masalar"
        subtitle="Restoran masalarınız"
        actions={
          <Group gap="sm">
            <Button
              variant="default"
              leftSection={<IconQrcode size={16} />}
              onClick={downloadAllQr}
              disabled={rows.length === 0}
            >
              {"Tüm QR'ları İndir"}
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => { setEditing(null); open(); }}
            >
              Yeni Masa
            </Button>
          </Group>
        }
      />
      {isLoading ? <Loader /> : (
        <ScrollArea>
          <Text size="xs" c="dimmed" mb={6}>
            {dragEnabled
              ? "Satırları tutamaçtan (⠿) sürükleyerek sırayı değiştirebilirsiniz."
              : "Sürükle-bırak sıralama için \"Sıra\" başlığına tıklayıp artan sıraya geçin."}
          </Text>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={36} />
                <SortableTh label="Sıra" sortKey="sort_order" active={sortKey === "sort_order"} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Ad" sortKey="name" active={sortKey === "name"} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Kapasite" sortKey="capacity" active={sortKey === "capacity"} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Durum" sortKey="state" active={sortKey === "state"} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Plan" sortKey="plan" active={sortKey === "plan"} dir={sortDir} onSort={handleSort} />
                <Table.Th>İlgili Garson</Table.Th>
                <Table.Th>Kapora (Override)</Table.Th>
                <SortableTh label="Aktif" sortKey="is_active" active={sortKey === "is_active"} dir={sortDir} onSort={handleSort} />
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((t: any) => {
                const planName = plans.find((p) => p.id === t.floor_plan)?.name;
                return (
                  <Table.Tr
                    key={t.id}
                    draggable={dragEnabled}
                    onDragStart={() => dragEnabled && setDragId(t.id)}
                    onDragOver={(e) => dragEnabled && dragId && e.preventDefault()}
                    onDrop={() => dragEnabled && handleDrop(t.id)}
                    onDragEnd={() => setDragId(null)}
                    {...(dragId === t.id ? { bg: "var(--mantine-color-blue-0)" } : {})}
                    style={{ opacity: dragId === t.id ? 0.5 : 1 }}
                  >
                    <Table.Td>
                      <IconGripVertical
                        size={16}
                        color="var(--mantine-color-gray-5)"
                        style={{ cursor: dragEnabled ? "grab" : "not-allowed" }}
                      />
                    </Table.Td>
                    <Table.Td c="dimmed">{t.sort_order ?? 0}</Table.Td>
                    <Table.Td fw={500}>🪑 {t.name}</Table.Td>
                    <Table.Td>{t.capacity} kişi</Table.Td>
                    <Table.Td><StatusBadge status={t.state} /></Table.Td>
                    <Table.Td>{planName || "-"}</Table.Td>
                    <Table.Td>
                      {t.assigned_waiter_name
                        ? (
                          <Badge
                            variant="light"
                            color="grape"
                            styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}
                          >
                            {t.assigned_waiter_name}
                          </Badge>
                        )
                        : "-"}
                    </Table.Td>
                    <Table.Td>
                      {t.deposit_override_minor
                        ? <MoneyDisplay minor={t.deposit_override_minor} />
                        : "-"}
                    </Table.Td>
                    <Table.Td>
                      {t.is_active
                        ? <Badge color="green">Aktif</Badge>
                        : <Badge color="gray">Pasif</Badge>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => downloadTableQr(t)}
                          aria-label="QR İndir"
                          title="QR kodunu PDF indir"
                        >
                          <IconDownload size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => { setEditing(t); open(); }}
                          aria-label="Düzenle"
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <Menu shadow="md" position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon variant="subtle" aria-label="Diğer işlemler">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconRefresh size={14} />}
                              onClick={() => handleRegenerateQr(t)}
                            >
                              {"QR'ı Yenile"}
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(t)}
                            >
                              Sil
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={10} ta="center" c="dimmed" py="xl">
                    {"Henüz masa tanımlanmadı. \"Yeni Masa\" ile başlayın."}
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="tables"
        title={editing ? "Masa Düzenle" : "Yeni Masa"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
        transform={(v) => ({
          // Boş "İlgili Garson" → null (DRF boş string'i geçersiz pk sayar)
          ...v,
          assigned_waiter: v.assigned_waiter || null,
        })}
      />
    </Stack>
  );
}
